/**
 * PetaCMS admin-web API client (Phase 2.5 cookie-auth 版).
 *
 * admin-api (http://localhost:8787 or VITE_API_BASE) への fetch。
 * 認証は HttpOnly Cookie (JWT access + refresh token) で自動送信。
 *
 * 方針:
 *  - 型安全: Zod スキーマ (packages/shared-types) で response を parse
 *  - エラー統一: ApiError に正規化して TanStack Query が判定できる形に
 *  - 認証: Cookie ベース (credentials: 'include')、401 時は自動で login redirect
 */

import type { z } from 'zod';
import { identifyUser, resetUser, trackEvent } from './posthog.js';

export const API_BASE =
  (import.meta as unknown as { env?: { VITE_API_BASE?: string } }).env?.VITE_API_BASE ??
  'http://localhost:8787';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    /** サーバー応答 JSON 全体 (PLAN_LIMIT_EXCEEDED の resource/current/limit 等の追加情報を拾うため) */
    public readonly payload?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** 402 PLAN_LIMIT_EXCEEDED 応答かどうか */
export function isPlanLimitError(error: unknown): error is ApiError & {
  payload: { resource: string; current: number; limit: number; planId: string };
} {
  return (
    error instanceof ApiError &&
    error.status === 402 &&
    error.code === 'PLAN_LIMIT_EXCEEDED' &&
    typeof error.payload?.resource === 'string'
  );
}

/** 403 PLAN_FEATURE_LOCKED 応答かどうか */
export function isPlanFeatureLockedError(error: unknown): error is ApiError & {
  payload: { feature: string; planId: string };
} {
  return (
    error instanceof ApiError &&
    error.status === 403 &&
    error.code === 'PLAN_FEATURE_LOCKED' &&
    typeof error.payload?.feature === 'string'
  );
}

// Auth 状態ヘルパー (HttpOnly Cookie は JS から読めないため localStorage フラグで管理)
const AUTH_FLAG_KEY = 'petacms_auth';

export function setAuthFlag(): void {
  try {
    localStorage.setItem(AUTH_FLAG_KEY, '1');
  } catch {
    // ignore
  }
}

export function clearAuthFlag(): void {
  try {
    localStorage.removeItem(AUTH_FLAG_KEY);
    localStorage.removeItem('petacms_jwt'); // Phase 1 legacy cleanup
  } catch {
    // ignore
  }
}

export function isAuthFlagSet(): boolean {
  try {
    return localStorage.getItem(AUTH_FLAG_KEY) === '1';
  } catch {
    return false;
  }
}

// ─── CSRF token management (review 006 + PR-CSRF-AUTOREFRESH) ────────────────

const UNSAFE_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);
let csrfTokenCache: string | null = null;

/**
 * CSRF token を取得 (キャッシュ優先)。
 *
 * PR-CSRF-AUTOREFRESH: /auth/csrf が 401 を返した場合、access token 失効が原因の
 * 可能性があるので auto refresh を試行 → 成功したら 1 回だけリトライする。
 * 401 以外 (5xx 等) や refresh 失敗時は ApiError(csrf_bootstrap_failed) を throw。
 *
 * 旧実装は 401 でも即 csrf_bootstrap_failed throw していたため、
 * セッション境界 (access expire 直後) で unsafe 送信が不安定になっていた。
 */
async function ensureCsrfToken(_isRetry = false): Promise<string> {
  if (csrfTokenCache) return csrfTokenCache;
  const res = await fetch(`${API_BASE}/auth/csrf`, { credentials: 'include' });
  if (res.status === 401 && !_isRetry) {
    const refreshed = await tryAutoRefresh();
    if (refreshed) return ensureCsrfToken(true);
  }
  if (!res.ok) throw new ApiError(res.status, 'csrf_bootstrap_failed', 'CSRF token の取得に失敗');
  const data = (await res.json()) as { csrfToken: string };
  csrfTokenCache = data.csrfToken;
  return csrfTokenCache;
}

/** Clear cached CSRF token (call on logout / password change). */
export function clearCsrfToken(): void {
  csrfTokenCache = null;
}

// ─── Auto refresh ───────────────────────────────────────────────────────────

let refreshPromise: Promise<boolean> | null = null;

/**
 * アクセストークン 401 時に自動リフレッシュを試みる。
 * 並行リクエストが同時に 401 を受けた場合、リフレッシュは 1 回だけ実行される。
 *
 * PR-CSRF-AUTOREFRESH: refresh 経路では admin-api 側で CSRF cookie を
 * 再発行しないため、cache をクリアする必要はない (旧実装は冗長な
 * /auth/csrf 再取得を強制していた)。
 */
async function tryAutoRefresh(): Promise<boolean> {
  if (refreshPromise) return refreshPromise;
  refreshPromise = (async () => {
    try {
      const res = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: { accept: 'application/json' },
      });
      return res.ok;
    } catch {
      return false;
    } finally {
      refreshPromise = null;
    }
  })();
  return refreshPromise;
}

// ─── API request types ──────────────────────────────────────────────────────

export interface ApiRequestOptions {
  /** parse と型付けを Zod に任せる場合に渡す */
  schema?: z.ZodTypeAny;
  /** HTTP method (default: GET) */
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  /** JSON body */
  body?: unknown;
  /** 任意の追加ヘッダ */
  headers?: Record<string, string>;
  /** 内部用: リフレッシュ後のリトライフラグ（無限ループ防止） */
  _isRetry?: boolean;
}

/**
 * 共通 fetch wrapper。
 * - Cookie ベース認証 (credentials: 'include')
 * - 401 時に auth flag クリア + login redirect
 * - JSON parse 失敗時の明示エラー
 * - Zod schema 検証
 */
export async function apiRequest<T>(path: string, options: ApiRequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET';

  const headers: Record<string, string> = {
    accept: 'application/json',
    ...(options.headers ?? {}),
  };
  if (options.body !== undefined) {
    headers['content-type'] = 'application/json';
  }
  // CSRF: unsafe method には X-CSRF-Token を自動付与 (review 006)
  if (UNSAFE_METHODS.has(method)) {
    headers['x-csrf-token'] = await ensureCsrfToken();
  }

  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    credentials: 'include',
    ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
  });

  let payload: unknown;
  try {
    payload = await res.json();
  } catch {
    throw new ApiError(res.status, 'invalid_json', 'サーバー応答を解析できませんでした');
  }

  if (!res.ok) {
    const err = payload as { error?: string; code?: string; message?: string };
    // 401 時: まずリフレッシュを試み、成功したらリトライ
    if (res.status === 401 && !path.startsWith('/auth/') && !options._isRetry) {
      const refreshed = await tryAutoRefresh();
      if (refreshed) {
        return apiRequest<T>(path, { ...options, _isRetry: true } as ApiRequestOptions);
      }
      clearAuthFlag();
      if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
        window.location.href = '/login';
      }
    }
    throw new ApiError(
      res.status,
      err.code ?? err.error ?? `http_${res.status}`,
      err.message ?? `Request failed: ${method} ${path}`,
      (payload as Record<string, unknown>) ?? undefined,
    );
  }

  if (options.schema) {
    const parsed = options.schema.safeParse(payload);
    if (!parsed.success) {
      throw new ApiError(
        200,
        'schema_mismatch',
        `レスポンスがスキーマと一致しません: ${parsed.error.message}`,
      );
    }
    return parsed.data as T;
  }

  return payload as T;
}

// -----------------------------------------------------------------------------
// Multipart upload (PR-CSRF-AUTOREFRESH)
//
// JSON 以外の body (multipart/form-data) を送信する経路。apiRequest と
// 同じ auto-refresh + CSRF retry 構造を共有して、CSV import や media
// upload で個別実装が drift しないようにする。
// -----------------------------------------------------------------------------

interface ApiUploadOptions {
  /** HTTP method (default: POST) */
  method?: 'POST' | 'PUT' | 'PATCH';
  /** FormData 本体 */
  body: FormData;
  /** 任意の追加ヘッダ (content-type は FormData が boundary 付きで自動セット) */
  headers?: Record<string, string>;
  /** 内部用: リフレッシュ後のリトライフラグ (無限ループ防止) */
  _isRetry?: boolean;
}

/**
 * 共通 multipart upload wrapper。
 * - Cookie 認証 (credentials: 'include')
 * - CSRF token を自動付与 (ensureCsrfToken は 401 で auto-refresh 経由)
 * - 401 時に refresh + 1 回リトライ
 * - レスポンスは JSON 前提 (data URL / blob 受信は別関数で書く)
 */
async function apiUploadRequest<T>(path: string, options: ApiUploadOptions): Promise<T> {
  const method = options.method ?? 'POST';
  const headers: Record<string, string> = {
    accept: 'application/json',
    ...(options.headers ?? {}),
    'x-csrf-token': await ensureCsrfToken(),
  };

  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    credentials: 'include',
    body: options.body,
  });

  let payload: unknown;
  try {
    payload = await res.json();
  } catch {
    throw new ApiError(res.status, 'invalid_json', 'サーバー応答を解析できませんでした');
  }

  if (!res.ok) {
    const err = payload as { error?: string; code?: string; message?: string };
    if (res.status === 401 && !options._isRetry) {
      const refreshed = await tryAutoRefresh();
      if (refreshed) {
        return apiUploadRequest<T>(path, { ...options, _isRetry: true });
      }
      clearAuthFlag();
      if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
        window.location.href = '/login';
      }
    }
    throw new ApiError(
      res.status,
      err.code ?? err.error ?? `http_${res.status}`,
      err.message ?? `Upload failed: ${method} ${path}`,
      (payload as Record<string, unknown>) ?? undefined,
    );
  }

  return payload as T;
}

// -----------------------------------------------------------------------------
// Domain types (admin-api の response shape と対応)
// -----------------------------------------------------------------------------

export interface MySiteRow {
  id: string;
  name: string;
  originHost: string;
  customHost: string | null;
  status: 'active' | 'suspended' | 'trialing' | 'expired' | 'sandbox';
  planId: string;
  rev: number;
  role: 'owner' | 'editor';
}

export interface SiteRow {
  id: string;
  ownerUserId: string;
  name: string;
  originHost: string;
  customHost: string | null;
  planId: string;
  rev: number;
  colorTheme: string | null;
  status: 'active' | 'suspended' | 'trialing' | 'expired' | 'sandbox';
  trialEndsAt: number | null;
  createdAt: number;
  updatedAt: number;
  deletedAt: number | null;
}

export interface CreateSitePayload {
  name: string;
  originHost: string;
  planId?: 'free' | 'starter' | 'pro' | 'agency' | 'enterprise';
  colorTheme?: string;
}

// -----------------------------------------------------------------------------
// API client functions (TanStack Query から呼ぶ)
// -----------------------------------------------------------------------------

/** GET /me/sites — 所属サイト一覧 */
export async function fetchMySites(): Promise<MySiteRow[]> {
  const res = await apiRequest<{ sites: MySiteRow[]; warning?: string }>('/me/sites');
  return res.sites;
}

/** POST /sites — 新規サイト作成 */
export async function createSite(payload: CreateSitePayload): Promise<{
  id: string;
  name: string;
  originHost: string;
  planId: string;
  status: string;
  trialEndsAt: number | null;
}> {
  return apiRequest('/sites', {
    method: 'POST',
    body: payload,
  });
}

/** POST /me/sandbox — サンドボックスサイト作成 (Phase 6 #12) */
export async function createSandbox(): Promise<{
  id: string;
  name: string;
  status: 'sandbox';
  sandboxExpiresAt: number;
  seededPosts: number;
  seededSnippets: number;
}> {
  return apiRequest('/me/sandbox', { method: 'POST' });
}

/** GET /me/sandbox — 既存サンドボックス概要 */
export async function fetchSandbox(): Promise<{
  sandbox: {
    id: string;
    name: string;
    status: 'sandbox';
    sandboxExpiresAt: number | null;
    createdAt: number;
  } | null;
}> {
  return apiRequest('/me/sandbox');
}

/** POST /sites/:siteId/verify-widget — ウィジェット設置検証 */
export async function verifyWidget(
  siteId: string,
): Promise<{ verified: boolean; reason?: string }> {
  return apiRequest(`/sites/${siteId}/verify-widget`, { method: 'POST' });
}

/** GET /sites/:siteId — サイト詳細 */
export async function fetchSite(siteId: string): Promise<SiteRow> {
  const res = await apiRequest<{ site: SiteRow }>(`/sites/${siteId}`);
  return res.site;
}

/** PATCH /sites/:siteId — サイト設定更新 */
export async function updateSite(
  siteId: string,
  payload: { name?: string; originHost?: string; colorTheme?: string | null },
): Promise<SiteRow> {
  const res = await apiRequest<{ site: SiteRow }>(`/sites/${siteId}`, {
    method: 'PATCH',
    body: payload,
  });
  return res.site;
}

// -----------------------------------------------------------------------------
// Posts (Phase 2 実装)
// -----------------------------------------------------------------------------

export type PostStatus = 'draft' | 'scheduled' | 'published' | 'unlisted' | 'trash';

/** 記事一覧レスポンスの 1 行 (admin-api の row shape に準拠) */
export interface PostRow {
  id: string;
  siteId: string;
  authorUserId: string;
  title: string;
  slug: string;
  excerpt: string | null;
  bodyJson: string;
  bodyHtmlCache: string | null;
  status: PostStatus;
  featuredMediaId: string | null;
  metaTitle: string | null;
  metaDescription: string | null;
  ogImageId: string | null;
  publishedAt: number | null;
  scheduledAt: number | null;
  pinned: number;
  rev: number;
  createdAt: number;
  updatedAt: number;
  deletedAt: number | null;
  /** 紐付いているカテゴリ ID の配列 (GET list / detail で返却) */
  categoryIds: string[];
}

export interface PostListParams {
  status?: PostStatus;
  q?: string;
  limit?: number;
  offset?: number;
  /** true でゴミ箱 (deleted_at IS NOT NULL) の記事を取得 */
  trash?: boolean;
}

export interface PostListResponse {
  posts: PostRow[];
  total: number;
  limit: number;
  offset: number;
}

/** GET /sites/:siteId/posts — 記事一覧 */
export async function fetchSitePosts(
  siteId: string,
  params: PostListParams = {},
): Promise<PostListResponse> {
  const qs = new URLSearchParams();
  if (params.status) qs.set('status', params.status);
  if (params.q) qs.set('q', params.q);
  if (params.limit !== undefined) qs.set('limit', String(params.limit));
  if (params.offset !== undefined) qs.set('offset', String(params.offset));
  if (params.trash) qs.set('trash', 'true');
  const suffix = qs.toString() ? `?${qs.toString()}` : '';

  try {
    return await apiRequest<PostListResponse>(`/sites/${siteId}/posts${suffix}`);
  } catch (e) {
    // schema 未適用 (503) 時は空で返して UI を壊さない
    if (e instanceof ApiError && e.status === 503) {
      return { posts: [], total: 0, limit: params.limit ?? 50, offset: params.offset ?? 0 };
    }
    throw e;
  }
}

export interface CreatePostPayload {
  title: string;
  slug?: string;
  excerpt?: string;
  bodyJson?: unknown;
  status?: PostStatus;
  scheduledAt?: number;
  metaTitle?: string;
  metaDescription?: string;
  pinned?: boolean;
  /**
   * カテゴリ ID の配列。省略時は未指定扱い:
   * - create: 空のまま作成
   * - update: 変更なし (既存紐付けを維持)
   * 配列を渡した場合はその値で完全置換される。
   */
  categoryIds?: string[];
}

/** POST /sites/:siteId/posts — 新規作成 */
export async function createPost(
  siteId: string,
  payload: CreatePostPayload,
): Promise<{
  id: string;
  siteId: string;
  slug: string;
  title: string;
  status: PostStatus;
  publishedAt: number | null;
  rev: number;
  createdAt: number;
  updatedAt: number;
}> {
  return apiRequest(`/sites/${siteId}/posts`, {
    method: 'POST',
    body: payload,
  });
}

/** GET /sites/:siteId/posts/:postId — 詳細取得 */
export async function fetchPost(siteId: string, postId: string): Promise<PostRow> {
  const res = await apiRequest<{ post: PostRow }>(`/sites/${siteId}/posts/${postId}`);
  return res.post;
}

/**
 * PATCH payload. `expectedRev` は楽観ロック用で必須。
 * サーバは WHERE id=? AND rev=? で更新し、一致しなければ 409 rev_conflict を返す。
 */
export type UpdatePostPayload = Partial<CreatePostPayload> & {
  expectedRev: number;
};

/**
 * PATCH /sites/:siteId/posts/:postId — 部分更新 (楽観ロック)
 *
 * 409 rev_conflict の場合、response body に `currentRev` が含まれる。
 * 呼び出し側は ApiError.status === 409 && code === 'rev_conflict' をハンドルして
 * 再読み込み → フォーム再 hydration → 再試行を促す。
 */
export async function updatePost(
  siteId: string,
  postId: string,
  payload: UpdatePostPayload,
): Promise<PostRow> {
  const res = await apiRequest<{ post: PostRow }>(`/sites/${siteId}/posts/${postId}`, {
    method: 'PATCH',
    body: payload,
  });
  return res.post;
}

/**
 * DELETE /sites/:siteId/posts/:postId?rev=N — ゴミ箱に移動 (楽観ロック)
 *
 * rev は query parameter で渡す (DELETE body は一部 proxy で strip される)。
 */
export async function deletePost(
  siteId: string,
  postId: string,
  expectedRev: number,
): Promise<{ ok: boolean; id: string; status: PostStatus; deletedAt: number }> {
  return apiRequest(`/sites/${siteId}/posts/${postId}?rev=${expectedRev}`, {
    method: 'DELETE',
  });
}

/**
 * DELETE /sites/:siteId/posts/:postId?hard=true — 完全削除 (owner 専用)
 *
 * 関連 post_categories / post_revisions も物理削除される。
 * R2 assets (featured / og 画像) の削除は未実装 (TODO: docs/todo/posts-trash-r2-cleanup.md)。
 */
export async function hardDeletePost(
  siteId: string,
  postId: string,
): Promise<{ ok: boolean; id: string; hard: true }> {
  return apiRequest(`/sites/${siteId}/posts/${postId}?hard=true`, {
    method: 'DELETE',
  });
}

/**
 * POST /sites/:siteId/posts/:postId/restore — ゴミ箱から復元
 *
 * deleted_at が null に戻り、status は 'draft' になる。
 * 対象が既に復元済み (deleted_at IS NULL) なら 404。
 */
export async function restorePost(siteId: string, postId: string): Promise<{ post: PostRow }> {
  return apiRequest(`/sites/${siteId}/posts/${postId}/restore`, {
    method: 'POST',
    body: {},
  });
}

/** POST /sites/:siteId/posts/:postId/publish — 即時公開 (楽観ロック) */
export async function publishPost(
  siteId: string,
  postId: string,
  expectedRev: number,
): Promise<{ ok: boolean; id: string; status: 'published'; publishedAt: number }> {
  return apiRequest(`/sites/${siteId}/posts/${postId}/publish`, {
    method: 'POST',
    body: { expectedRev },
  });
}

/**
 * POST /sites/:siteId/posts/:postId/preview-url — 下書きプレビュー URL を発行
 *
 * 返り値は 1 時間有効な短命 URL (署名付き)。
 */
export async function createPostPreviewUrl(
  siteId: string,
  postId: string,
): Promise<{ url: string; expiresAt: string }> {
  return apiRequest(`/sites/${siteId}/posts/${postId}/preview-url`, {
    method: 'POST',
  });
}

// -----------------------------------------------------------------------------
// Post revisions (バージョン履歴, Feature #16)
// -----------------------------------------------------------------------------

export type RevisionChangeKind = 'create' | 'update' | 'publish' | 'restore';

export interface RevisionSummary {
  id: string;
  postId: string;
  rev: number;
  title: string;
  status: PostStatus;
  authorUserId: string;
  changeKind: RevisionChangeKind;
  createdAt: number;
}

export interface RevisionDetail extends RevisionSummary {
  excerpt: string | null;
  bodyJson: string;
  metaTitle: string | null;
  metaDescription: string | null;
  featuredMediaId: string | null;
  ogImageId: string | null;
}

/** GET /sites/:siteId/posts/:postId/revisions */
export async function fetchPostRevisions(
  siteId: string,
  postId: string,
): Promise<RevisionSummary[]> {
  const res = await apiRequest<{ revisions: RevisionSummary[]; total: number }>(
    `/sites/${siteId}/posts/${postId}/revisions`,
  );
  return res.revisions;
}

/** GET /sites/:siteId/posts/:postId/revisions/:revisionId */
export async function fetchPostRevision(
  siteId: string,
  postId: string,
  revisionId: string,
): Promise<RevisionDetail> {
  const res = await apiRequest<{ revision: RevisionDetail }>(
    `/sites/${siteId}/posts/${postId}/revisions/${revisionId}`,
  );
  return res.revision;
}

/** POST /sites/:siteId/posts/:postId/restore/:revisionId — 楽観ロック必須 */
export async function restorePostRevision(
  siteId: string,
  postId: string,
  revisionId: string,
  expectedRev: number,
): Promise<{ post: PostRow }> {
  return apiRequest(`/sites/${siteId}/posts/${postId}/restore/${revisionId}`, {
    method: 'POST',
    body: { expectedRev },
  });
}

// -----------------------------------------------------------------------------
// API Keys (Feature #17 Phase C)
// -----------------------------------------------------------------------------

export interface ApiKeyRow {
  id: string;
  name: string;
  prefix: string;
  scopes: string;
  lastUsedAt: number | null;
  expiresAt: number;
  createdAt: number;
}

export interface CreateApiKeyPayload {
  name: string;
  scopes?: string;
  expiresAt?: number;
}

export async function fetchApiKeys(siteId: string): Promise<ApiKeyRow[]> {
  const res = await apiRequest<{ apiKeys: ApiKeyRow[] }>(`/sites/${siteId}/api-keys`);
  return res.apiKeys;
}

export async function createApiKey(
  siteId: string,
  payload: CreateApiKeyPayload,
): Promise<{ apiKey: ApiKeyRow; key: string }> {
  return apiRequest(`/sites/${siteId}/api-keys`, {
    method: 'POST',
    body: payload,
  });
}

export async function deleteApiKey(siteId: string, keyId: string): Promise<void> {
  await apiRequest(`/sites/${siteId}/api-keys/${keyId}`, { method: 'DELETE' });
}

// -----------------------------------------------------------------------------
// Webhooks (Feature #17 Phase A)
// -----------------------------------------------------------------------------

export type WebhookEventType =
  | 'post.created'
  | 'post.updated'
  | 'post.published'
  | 'post.deleted'
  | 'post.restored';

export const WEBHOOK_EVENT_TYPES: WebhookEventType[] = [
  'post.created',
  'post.updated',
  'post.published',
  'post.deleted',
  'post.restored',
];

export interface WebhookRow {
  id: string;
  name: string;
  url: string;
  events: WebhookEventType[];
  enabled: boolean;
  failureCount: number;
  lastSuccessAt: number | null;
  lastErrorAt: number | null;
  lastErrorMessage: string | null;
  /** マスクされた表示 (作成直後のみフルで返る) */
  secret: string;
  createdAt: number;
  updatedAt: number;
}

export interface CreateWebhookPayload {
  name: string;
  url: string;
  events: WebhookEventType[];
  enabled?: boolean;
}

export interface WebhookDeliveryRow {
  id: string;
  webhookId: string;
  eventId: string;
  eventType: WebhookEventType;
  attempt: number;
  status: 'success' | 'failed' | 'pending';
  responseStatus: number | null;
  errorMessage: string | null;
  durationMs: number | null;
  createdAt: number;
}

export async function fetchWebhooks(siteId: string): Promise<WebhookRow[]> {
  const res = await apiRequest<{ webhooks: WebhookRow[] }>(`/sites/${siteId}/webhooks`);
  return res.webhooks;
}

export async function createWebhook(
  siteId: string,
  payload: CreateWebhookPayload,
): Promise<WebhookRow> {
  const res = await apiRequest<{ webhook: WebhookRow }>(`/sites/${siteId}/webhooks`, {
    method: 'POST',
    body: payload,
  });
  return res.webhook;
}

export async function updateWebhook(
  siteId: string,
  webhookId: string,
  payload: Partial<CreateWebhookPayload>,
): Promise<WebhookRow> {
  const res = await apiRequest<{ webhook: WebhookRow }>(`/sites/${siteId}/webhooks/${webhookId}`, {
    method: 'PATCH',
    body: payload,
  });
  return res.webhook;
}

export async function deleteWebhook(siteId: string, webhookId: string): Promise<void> {
  await apiRequest(`/sites/${siteId}/webhooks/${webhookId}`, { method: 'DELETE' });
}

export async function testWebhook(
  siteId: string,
  webhookId: string,
): Promise<{ ok: boolean; eventId: string }> {
  return apiRequest(`/sites/${siteId}/webhooks/${webhookId}/test`, { method: 'POST' });
}

export async function fetchWebhookDeliveries(
  siteId: string,
  webhookId: string,
): Promise<WebhookDeliveryRow[]> {
  const res = await apiRequest<{ deliveries: WebhookDeliveryRow[] }>(
    `/sites/${siteId}/webhooks/${webhookId}/deliveries`,
  );
  return res.deliveries;
}

// -----------------------------------------------------------------------------
// Members (Phase 7-B)
// -----------------------------------------------------------------------------

export interface MemberRow {
  membershipId: string;
  userId: string;
  email: string;
  role: 'owner' | 'editor';
  acceptedAt: number | null;
  createdAt: number;
}

export interface InviteSiteMemberPayload {
  email: string;
  role?: 'editor';
}

export interface InviteSiteMemberResult {
  ok: boolean;
  invitationId: string;
  email: string;
  role: 'editor';
  expiresAt: number;
  emailSent: boolean;
}

export interface AcceptInvitationResult {
  ok: boolean;
  siteId: string;
  role: 'editor';
  status: 'joined' | 'restored' | 'already_member';
}

/** GET /sites/:siteId/members — メンバー一覧 */
export async function fetchSiteMembers(siteId: string): Promise<MemberRow[]> {
  try {
    const res = await apiRequest<{ members: MemberRow[] }>(`/sites/${siteId}/members`);
    return res.members;
  } catch (e) {
    if (e instanceof ApiError && e.status === 503) return [];
    throw e;
  }
}

/** POST /sites/:siteId/members/invite — メンバー招待 */
export async function inviteSiteMember(
  siteId: string,
  payload: InviteSiteMemberPayload,
): Promise<InviteSiteMemberResult> {
  return apiRequest(`/sites/${siteId}/members/invite`, {
    method: 'POST',
    body: {
      email: payload.email,
      role: payload.role ?? 'editor',
    },
  });
}

/** PATCH /sites/:siteId/members/:memberId/role — ロール変更 */
export async function updateSiteMemberRole(
  siteId: string,
  memberId: string,
  role: 'owner' | 'editor',
): Promise<{ ok: boolean; membershipId: string; role: 'owner' | 'editor' }> {
  return apiRequest(`/sites/${siteId}/members/${memberId}/role`, {
    method: 'PATCH',
    body: { role },
  });
}

/** DELETE /sites/:siteId/members/:memberId — メンバー削除 */
export async function deleteSiteMember(
  siteId: string,
  memberId: string,
): Promise<{ ok: boolean; membershipId: string; deletedAt: number }> {
  return apiRequest(`/sites/${siteId}/members/${memberId}`, {
    method: 'DELETE',
  });
}

/** POST /invitations/accept — 招待承諾 */
export async function acceptInvitation(token: string): Promise<AcceptInvitationResult> {
  return apiRequest('/invitations/accept', {
    method: 'POST',
    body: { token },
  });
}

// -----------------------------------------------------------------------------
// Hostnames (Phase 2 実装)
// -----------------------------------------------------------------------------

export interface HostnameValidation {
  txtName: string;
  txtValue: string;
  helpHtml: string;
}

export interface SiteHostnameRow {
  id: string;
  siteId: string;
  hostname: string;
  cfHostnameId: string;
  sslStatus: 'pending' | 'active' | 'failed';
  dcvStatus: string;
  createdAt: number;
  verifiedAt: number | null;
  deletedAt: number | null;
}

export async function fetchSiteHostnames(siteId: string): Promise<SiteHostnameRow[]> {
  try {
    const res = await apiRequest<{ hostnames: SiteHostnameRow[] }>(`/sites/${siteId}/hostnames`);
    return res.hostnames;
  } catch (e) {
    if (e instanceof ApiError && e.status === 503) return [];
    throw e;
  }
}

export async function createSiteHostname(
  siteId: string,
  hostname: string,
): Promise<{ ok: boolean; hostname: SiteHostnameRow; validation: HostnameValidation | null }> {
  return apiRequest(`/sites/${siteId}/hostnames`, {
    method: 'POST',
    body: { hostname },
  });
}

export async function fetchSiteHostnameStatus(
  siteId: string,
  hostnameId: string,
): Promise<{
  hostname: SiteHostnameRow;
  validation: HostnameValidation | null;
  stale?: boolean;
}> {
  return apiRequest(`/sites/${siteId}/hostnames/${hostnameId}/status`);
}

export async function recheckSiteHostname(
  siteId: string,
  hostnameId: string,
): Promise<{ ok: boolean; hostname: SiteHostnameRow; validation: HostnameValidation | null }> {
  return apiRequest(`/sites/${siteId}/hostnames/${hostnameId}/recheck`, {
    method: 'POST',
  });
}

export async function deleteSiteHostname(
  siteId: string,
  hostnameId: string,
): Promise<{ ok: boolean; id: string; deletedAt: number }> {
  return apiRequest(`/sites/${siteId}/hostnames/${hostnameId}`, {
    method: 'DELETE',
  });
}

// -----------------------------------------------------------------------------
// Categories (Phase 2 実装)
// -----------------------------------------------------------------------------

export interface CategoryRow {
  id: string;
  siteId: string;
  parentId: string | null;
  slug: string;
  name: string;
  rev: number;
  createdAt: number;
}

export interface CreateCategoryPayload {
  name: string;
  slug: string;
  parentId?: string | null;
}

export type UpdateCategoryPayload = Partial<CreateCategoryPayload>;

/** GET /sites/:siteId/categories — カテゴリ一覧 */
export async function fetchSiteCategories(siteId: string): Promise<CategoryRow[]> {
  try {
    const res = await apiRequest<{ categories: CategoryRow[] }>(`/sites/${siteId}/categories`);
    return res.categories;
  } catch (e) {
    if (e instanceof ApiError && e.status === 503) return [];
    throw e;
  }
}

/** POST /sites/:siteId/categories — 新規作成 */
export async function createCategory(
  siteId: string,
  payload: CreateCategoryPayload,
): Promise<CategoryRow> {
  return apiRequest(`/sites/${siteId}/categories`, {
    method: 'POST',
    body: payload,
  });
}

/** PATCH /sites/:siteId/categories/:categoryId — 部分更新 */
export async function updateCategory(
  siteId: string,
  categoryId: string,
  payload: UpdateCategoryPayload,
): Promise<CategoryRow> {
  const res = await apiRequest<{ category: CategoryRow }>(
    `/sites/${siteId}/categories/${categoryId}`,
    { method: 'PATCH', body: payload },
  );
  return res.category;
}

/** DELETE /sites/:siteId/categories/:categoryId */
export async function deleteCategory(
  siteId: string,
  categoryId: string,
): Promise<{ ok: boolean; id: string }> {
  return apiRequest(`/sites/${siteId}/categories/${categoryId}`, {
    method: 'DELETE',
  });
}

// -----------------------------------------------------------------------------
// Snippets (Phase 2 実装)
// -----------------------------------------------------------------------------

export interface SnippetRow {
  id: string;
  siteId: string;
  key: string;
  contentText: string | null;
  contentHtml: string | null;
  templateHtml: string | null;
  rev: number;
  updatedBy: string;
  createdAt: number;
  updatedAt: number;
}

/** GET /sites/:siteId/snippets — スニペット一覧 */
export async function fetchSiteSnippets(siteId: string): Promise<SnippetRow[]> {
  try {
    const res = await apiRequest<{ snippets: SnippetRow[] }>(`/sites/${siteId}/snippets`);
    return res.snippets;
  } catch (e) {
    if (e instanceof ApiError && e.status === 503) return [];
    throw e;
  }
}

export interface SnippetUpsertPayload {
  contentText?: string;
  contentHtml?: string;
  templateHtml?: string;
}

/** PUT /sites/:siteId/snippets/:key — upsert */
export async function upsertSnippet(
  siteId: string,
  key: string,
  payload: SnippetUpsertPayload,
): Promise<{
  id: string;
  key: string;
  action: 'created' | 'updated';
  createdAt?: number;
  updatedAt: number;
}> {
  return apiRequest(`/sites/${siteId}/snippets/${encodeURIComponent(key)}`, {
    method: 'PUT',
    body: payload,
  });
}

/** DELETE /sites/:siteId/snippets/:key */
export async function deleteSnippet(
  siteId: string,
  key: string,
): Promise<{ ok: boolean; key: string }> {
  return apiRequest(`/sites/${siteId}/snippets/${encodeURIComponent(key)}`, {
    method: 'DELETE',
  });
}

// -----------------------------------------------------------------------------
// Auth (Phase 2.5 — Cookie ベース)
// -----------------------------------------------------------------------------

export interface AuthUser {
  id: string;
  email: string;
  emailVerified: boolean;
  createdAt: number;
  saRole?: 'admin' | 'operator' | 'auditor' | null;
}

/** POST /auth/login — ログイン (Cookie 自動セット) */
export async function authLogin(
  email: string,
  password: string,
): Promise<{ ok: boolean; userId: string; email: string; emailVerified: boolean }> {
  const result = await apiRequest<{
    ok: boolean;
    userId: string;
    email: string;
    emailVerified: boolean;
  }>('/auth/login', {
    method: 'POST',
    body: { email, password },
  });
  setAuthFlag();
  identifyUser(result.userId);
  trackEvent('login_success');
  return result;
}

/** POST /auth/magic-link/request — Magic Link メール送信要求 (ADR-0013) */
export async function authMagicLinkRequest(email: string): Promise<{ ok: boolean }> {
  return apiRequest<{ ok: boolean }>('/auth/magic-link/request', {
    method: 'POST',
    body: { email },
  });
}

/** POST /auth/magic-link/verify — Magic Link トークン検証 (ADR-0013) */
export async function authMagicLinkVerify(
  token: string,
): Promise<{ ok: boolean; userId: string; email: string; emailVerified: boolean }> {
  const result = await apiRequest<{
    ok: boolean;
    userId: string;
    email: string;
    emailVerified: boolean;
  }>('/auth/magic-link/verify', {
    method: 'POST',
    body: { token },
  });
  setAuthFlag();
  identifyUser(result.userId);
  trackEvent('login_success');
  return result;
}

/** Google OAuth 認可 URL (ADR-0013)。window.location 移動用。 */
export function googleOAuthAuthorizeUrl(): string {
  return `${API_BASE}/auth/google/authorize`;
}

/** POST /auth/logout — ログアウト (Cookie クリア) */
export async function authLogout(): Promise<void> {
  try {
    await apiRequest('/auth/logout', { method: 'POST' });
  } catch {
    // Ignore errors during logout
  }
  trackEvent('logout');
  clearAuthFlag();
  clearCsrfToken();
  resetUser();
}

/** GET /auth/me — 現在のユーザー情報 */
export async function authMe(): Promise<AuthUser> {
  const res = await apiRequest<{ user: AuthUser }>('/auth/me');
  return res.user;
}

/** POST /auth/register — 新規登録 */
export async function authRegister(
  email: string,
  password: string,
): Promise<{ ok: boolean; userId: string; message: string }> {
  return apiRequest('/auth/register', {
    method: 'POST',
    body: { email, password },
  });
}

/** POST /auth/refresh — トークンリフレッシュ */
export async function authRefresh(): Promise<{ ok: boolean }> {
  return apiRequest('/auth/refresh', { method: 'POST' });
}

/** POST /auth/forgot-password — パスワードリセットメール送信 */
export async function authForgotPassword(email: string): Promise<{ ok: boolean; message: string }> {
  return apiRequest('/auth/forgot-password', {
    method: 'POST',
    body: { email },
  });
}

/** POST /auth/reset-password — パスワード再設定 */
export async function authResetPassword(
  token: string,
  password: string,
): Promise<{ ok: boolean; message: string }> {
  return apiRequest('/auth/reset-password', {
    method: 'POST',
    body: { token, password },
  });
}

/** GET /auth/verify-email?token=xxx — メール確認 */
/** POST /auth/verify-email — メール確認 (review 006: POST に統一) */
export async function authVerifyEmail(token: string): Promise<{ ok: boolean; message: string }> {
  return apiRequest('/auth/verify-email', { method: 'POST', body: { token } });
}

/** POST /auth/sudo — sudo mode 有効化 (パスワード再入力 → 5分間有効) */
export async function authSudo(password: string): Promise<{ ok: boolean; sudoExpiresAt: number }> {
  return apiRequest('/auth/sudo', {
    method: 'POST',
    body: { password },
  });
}

/** POST /auth/change-password — パスワード変更 (要 sudo) */
export async function authChangePassword(
  currentPassword: string,
  newPassword: string,
): Promise<{ ok: boolean; message: string }> {
  return apiRequest('/auth/change-password', {
    method: 'POST',
    body: { currentPassword, newPassword },
  });
}

// -----------------------------------------------------------------------------
// Media (Phase 2 実装)
// -----------------------------------------------------------------------------

export interface MediaRow {
  id: string;
  siteId: string;
  filename: string;
  mimeType: string;
  size: number;
  r2Key: string;
  url: string;
  width: number | null;
  height: number | null;
  alt: string | null;
  uploadedBy: string;
  createdAt: number;
}

export interface MediaListResponse {
  media: MediaRow[];
  total: number;
  limit: number;
  offset: number;
}

/** GET /sites/:siteId/media — メディア一覧 */
export async function fetchSiteMedia(
  siteId: string,
  params: { limit?: number; offset?: number } = {},
): Promise<MediaListResponse> {
  const qs = new URLSearchParams();
  if (params.limit !== undefined) qs.set('limit', String(params.limit));
  if (params.offset !== undefined) qs.set('offset', String(params.offset));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return apiRequest(`/sites/${siteId}/media${suffix}`);
}

/** POST /sites/:siteId/media — メディアアップロード (multipart) */
export async function uploadMedia(siteId: string, file: File): Promise<MediaRow> {
  // PR-CSRF-AUTOREFRESH: 共通 apiUploadRequest 経由で CSRF + auto-refresh 統合
  const formData = new FormData();
  formData.append('file', file);
  const payload = await apiUploadRequest<{ media: MediaRow }>(`/sites/${siteId}/media`, {
    body: formData,
  });
  return payload.media;
}

/** DELETE /sites/:siteId/media/:mediaId */
export async function deleteMedia(siteId: string, mediaId: string): Promise<{ ok: boolean }> {
  return apiRequest(`/sites/${siteId}/media/${mediaId}`, { method: 'DELETE' });
}

// ─────────────────────────────────────────────────────────
// Billing
// ─────────────────────────────────────────────────────────

export interface BillingSubscription {
  site: {
    status: 'active' | 'suspended' | 'trialing' | 'expired' | 'sandbox';
    planId: string;
    trialEndsAt: number | null;
  } | null;
  subscription: {
    status: string;
    planId: string;
    currentPeriodEnd: number;
    cancelAtPeriodEnd: number;
  } | null;
}

/** GET /billing/subscription?siteId=xxx */
export async function fetchBillingSubscription(siteId: string): Promise<BillingSubscription> {
  return apiRequest(`/billing/subscription?siteId=${siteId}`);
}

/** POST /billing/checkout — Stripe Checkout URL を取得
 *
 * 2026-04-25: Pro プラン追加・月額/年額・Agency 超過サイト数対応。
 * API 側スキーマ（apps/admin-api/src/routes/billing.ts CheckoutSchema）に合わせ、
 *   plan: 'starter' | 'pro' | 'agency'
 *   interval: 'month' | 'year'（default: 'month'）
 *   extraSites: 0–100（agency 専用、starter/pro では無視される）
 * を送信する。
 */
export async function createCheckoutSession(
  siteId: string,
  plan: 'starter' | 'pro' | 'agency',
  options?: { interval?: 'month' | 'year'; extraSites?: number },
): Promise<{ url: string }> {
  const interval = options?.interval ?? 'month';
  const extraSites = options?.extraSites ?? 0;
  return apiRequest('/billing/checkout', {
    method: 'POST',
    body: {
      siteId,
      plan,
      interval,
      extraSites,
      successUrl: `${window.location.origin}/sites/${siteId}/settings/billing?success=1`,
      cancelUrl: `${window.location.origin}/sites/${siteId}/settings/billing?canceled=1`,
    },
  });
}

/** POST /billing/portal — Stripe Customer Portal URL を取得 */
export async function createPortalSession(siteId: string): Promise<{ url: string }> {
  return apiRequest('/billing/portal', {
    method: 'POST',
    body: {
      siteId,
      returnUrl: `${window.location.origin}/sites/${siteId}/settings/billing`,
    },
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Audit Logs (Phase 2 実装)
// ─────────────────────────────────────────────────────────────────────────────

export interface AuditLogRow {
  id: string;
  action: string;
  targetType: string;
  targetId: string | null;
  actorUserId: string | null;
  metadataJson: string | null;
  createdAt: number;
}

export interface AuditLogsListParams {
  limit?: number;
  offset?: number;
  action?: string;
  days?: number;
}

export interface AuditLogsListResponse {
  logs: AuditLogRow[];
  total: number;
  limit: number;
  offset: number;
}

/** GET /sites/:siteId/audit-logs — 監査ログ一覧 */
export async function fetchAuditLogs(
  siteId: string,
  params: AuditLogsListParams = {},
): Promise<AuditLogsListResponse> {
  const qs = new URLSearchParams();
  if (params.limit !== undefined) qs.set('limit', String(params.limit));
  if (params.offset !== undefined) qs.set('offset', String(params.offset));
  if (params.action) qs.set('action', params.action);
  if (params.days !== undefined) qs.set('days', String(params.days));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';

  return apiRequest<AuditLogsListResponse>(`/sites/${siteId}/audit-logs${suffix}`);
}

// -----------------------------------------------------------------------------
// Import (Phase 7-C WordPress 移行インポーター)
// -----------------------------------------------------------------------------

export type ImportJobStatus = 'pending' | 'running' | 'completed' | 'failed';

export interface ImportJobRow {
  id: string;
  source: string;
  sourceUrl: string;
  status: ImportJobStatus;
  processedCount: number;
  totalCount: number | null;
  errorLog: string | null;
  options: {
    importMedia?: boolean;
    importCategories?: boolean;
    postLimit?: number;
  } | null;
  createdAt: number;
  completedAt: number | null;
  createdBy: string;
}

export interface StartWpImportPayload {
  wpUrl: string;
  importMedia?: boolean;
  importCategories?: boolean;
  postLimit?: number;
}

export interface StartWpImportResponse {
  jobId: string;
  status: ImportJobStatus;
  totalPosts: number | null;
}

/** POST /sites/:siteId/import/wp — WP インポートジョブ起票 (202 Accepted) */
export async function startWpImport(
  siteId: string,
  payload: StartWpImportPayload,
): Promise<StartWpImportResponse> {
  return apiRequest(`/sites/${siteId}/import/wp`, {
    method: 'POST',
    body: payload,
  });
}

/** GET /sites/:siteId/import/jobs — 自サイトのジョブ一覧 */
export async function fetchImportJobs(siteId: string): Promise<ImportJobRow[]> {
  try {
    const res = await apiRequest<{ jobs: ImportJobRow[] }>(`/sites/${siteId}/import/jobs`);
    return res.jobs;
  } catch (e) {
    if (e instanceof ApiError && e.status === 503) return [];
    throw e;
  }
}

/** GET /sites/:siteId/import/jobs/:jobId — 1 件詳細 (ポーリング用) */
export async function fetchImportJob(siteId: string, jobId: string): Promise<ImportJobRow> {
  const res = await apiRequest<{ job: ImportJobRow }>(`/sites/${siteId}/import/jobs/${jobId}`);
  return res.job;
}

export interface StartCsvImportResponse {
  jobId: string;
  status: ImportJobStatus;
  rowCount: number;
}

/**
 * POST /sites/:siteId/import/csv — CSV 手動インポート (Phase 7-D)
 *
 * PR-CSRF-AUTOREFRESH: 旧実装は /auth/csrf を独自に叩いていたため、auto-refresh の
 * 経路から外れていた。現行は共通 apiUploadRequest 経由で CSRF + 401 リトライを
 * 共有。content-type は FormData に任せる (boundary 自動付与)。
 */
export async function startCsvImport(
  siteId: string,
  file: File,
  opts: { hasHeader?: boolean } = {},
): Promise<StartCsvImportResponse> {
  const form = new FormData();
  form.append('file', file);
  if (opts.hasHeader === false) {
    form.append('hasHeader', 'false');
  }
  return apiUploadRequest<StartCsvImportResponse>(`/sites/${siteId}/import/csv`, { body: form });
}
// -----------------------------------------------------------------------------
// Site Export (Feature #18)
// -----------------------------------------------------------------------------

export type ExportJobStatus = 'pending' | 'running' | 'completed' | 'failed' | 'expired';

export interface ExportJobRow {
  id: string;
  status: ExportJobStatus;
  bytesTotal: number | null;
  /** 完了かつ有効期限内のときのみ、download 相対パスが入る。期限切れ等は null */
  downloadUrl: string | null;
  downloadUrlExpiresAt: number | null;
  errorLog: string | null;
  createdAt: number;
  completedAt: number | null;
}

/** POST /sites/:siteId/export — エクスポート開始 (202 Accepted) */
export async function startSiteExport(
  siteId: string,
): Promise<{ jobId: string; status: ExportJobStatus }> {
  return apiRequest(`/sites/${siteId}/export`, { method: 'POST' });
}

/** GET /sites/:siteId/exports — 履歴 (最新 10 件) */
export async function fetchSiteExports(siteId: string): Promise<ExportJobRow[]> {
  const res = await apiRequest<{ jobs: ExportJobRow[] }>(`/sites/${siteId}/exports`);
  return res.jobs;
}

/** GET /sites/:siteId/exports/:jobId — 単体取得 */
export async function fetchSiteExport(siteId: string, jobId: string): Promise<ExportJobRow> {
  const res = await apiRequest<{ job: ExportJobRow }>(`/sites/${siteId}/exports/${jobId}`);
  return res.job;
}

/** 完全な download URL (API_BASE を prepend) を返すヘルパ */
export function exportDownloadHref(downloadUrl: string | null): string | null {
  if (!downloadUrl) return null;
  if (downloadUrl.startsWith('http://') || downloadUrl.startsWith('https://')) return downloadUrl;
  return `${API_BASE}${downloadUrl}`;
}

// -----------------------------------------------------------------------------
// Plan usage (GET /sites/:siteId/usage)
// -----------------------------------------------------------------------------

/** admin-api の SiteUsageResponse と対応。limit は 'unlimited' 文字列で返ってくる */
export interface SiteUsageMetric {
  current: number;
  limit: number | 'unlimited';
}

export interface SiteUsage {
  planId: string;
  quantity: number;
  limits: {
    sites: SiteUsageMetric;
    posts: SiteUsageMetric;
    media_bytes: SiteUsageMetric;
    members: SiteUsageMetric;
    webhooks: SiteUsageMetric;
    custom_domains: SiteUsageMetric;
  };
  features: {
    audit_csv_export: boolean;
    status_api: boolean;
    image_cdn: boolean;
  };
}

/** サイトの現在使用量 + プラン上限 + 機能有効状態を取得。PlanLimitBanner 用。 */
export async function fetchSiteUsage(siteId: string): Promise<SiteUsage> {
  return apiRequest<SiteUsage>(`/sites/${encodeURIComponent(siteId)}/usage`);
}
